import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CloseCircleIcon, HideShowIcon } from "../../../../../components/icons";
import DiscardNoticeModal from "./DiscardNoticeModal";

const BookingStatus = ({
    authDetailBooking,
    onDiscardSuccess,
}) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const [noticeOpen, setNoticeOpen] = useState(false);
    const [noticeShow, setNoticeShow] = useState(true);
    const [discardModalOpen, setDiscardModalOpen] = useState(false);

    const getStatusBooking = () => {
        if (authDetailBooking?.bookingStatus === "CANCELLED") {
            return {
                bgColor: "#FFEAE7",
                textColor: "#EA4335",
                text: t("bookingDetail.status.cancelled"),
                hasRequest: false
            };
        } else {
            // Get current time in JST (UTC + 9 hours)
            const currentUTC = Date.now();
            const currentJST = new Date(currentUTC + 9 * 60 * 60 * 1000);

            // Parse endTime (garageArivalTime is UTC-based ISO)
            const endTime = new Date(authDetailBooking?.garageArivalTime);

            if (currentJST > endTime) {
                return {
                    bgColor: "#D8ECD1",
                    textColor: "#3C7120",
                    text: t("bookingDetail.status.completed"),
                    hasRequest: false
                }
            }

            if (authDetailBooking?.paymentStatus === "CHARGE_PENDING") {
                let hasRequest = false;
                if (authDetailBooking?.requestChangeBooking?.some(item => item?.status === "PENDING_APPROVAL") || (authDetailBooking?.requestChangeBooking[0]?.status === "REJECTED" && authDetailBooking?.requestChangeBooking[0]?.showRejection)) {
                    hasRequest = true;
                }

                return {
                    bgColor: "#F6BB2A",
                    textColor: "#FFFFFF",
                    text: t("bookingDetail.status.pendingConfirmation"),
                    hasRequest: hasRequest
                }
            } else if (authDetailBooking?.paymentStatus === "CHARGED" || authDetailBooking?.paymentStatus === "ADDED_TO_MONTHLY_TOTAL") {
                let hasRequest = false;
                if (authDetailBooking?.requestChangeBooking?.some(item => item?.status === "PENDING_APPROVAL") || (authDetailBooking?.requestChangeBooking[0]?.status === "REJECTED" && authDetailBooking?.requestChangeBooking[0]?.showRejection)) {
                    hasRequest = true;
                }
                return {
                    bgColor: "#3CAEA3",
                    textColor: "#FFFFFF",
                    text: t("bookingDetail.status.confirmed"),
                    hasRequest: hasRequest
                }
            }
        }

        return {
            bgColor: "#F6BB2A",
            textColor: "#FFFFFF",
            text: t("bookingDetail.status.pendingConfirmation"),
            hasRequest: false
        }
    }

    const getRequestChangeStatus = () => {
        if (authDetailBooking?.requestChangeBooking[0].status === "PENDING_APPROVAL") {
            return {
                bgColor: "#20639B1A",
                textColor: "#20639B",
                text: t("bookingDetail.status.changeRequested"),
            }
        }

        if (authDetailBooking?.requestChangeBooking[0].status === "REJECTED" && authDetailBooking?.requestChangeBooking[0]?.showRejection) {
            return {
                bgColor: "#FFEAE7",
                textColor: "#EA4335",
                text: t("bookingDetail.status.changeRejected"),
            }
        }
    }

    const isRejected = authDetailBooking?.requestChangeBooking[0]?.status === "REJECTED" && authDetailBooking?.requestChangeBooking[0]?.showRejection;

    return (
        <div className="tw:flex tw:w-full tw:flex-col tw:gap-1.5 tw:lg:w-[340px]">
            {/* Status Badge */}
            <div className={`tw:flex tw:h-[45px] tw:items-center tw:justify-center tw:rounded-[5px] tw:bg-[${getStatusBooking().bgColor}] tw:px-6 tw:py-[5px]`}>
                <span className={`tw:font-hanken-grotesk tw:text-[15px] tw:font-normal tw:leading-[22px] tw:!text-[${getStatusBooking().textColor}]`}>
                    {getStatusBooking().text}
                </span>
            </div>

            {
                getStatusBooking().hasRequest && (
                    <div className={`tw:flex tw:h-[45px] tw:items-center tw:justify-center tw:rounded-[5px] tw:!bg-[${getRequestChangeStatus().bgColor}] tw:!border tw:!border-[${getRequestChangeStatus().bgColor}] tw:!border-t-0 tw:!border-b-0 tw:px-6 tw:py-[5px]`}>
                        <span className={`tw:font-hanken-grotesk tw:text-[15px] tw:font-normal tw:leading-[22px] tw:!text-[${getRequestChangeStatus().textColor}]`}>
                            {getRequestChangeStatus().text}
                        </span>
                    </div>
                )
            }

            {/* Important Notice */}
            {(isRejected && noticeShow) && (
                <div className="tw:flex tw:flex-col tw:rounded-[5px] tw:border tw:border-[#EA4335] tw:overflow-hidden">
                    {/* Header */}
                    <div className="tw:flex tw:items-center tw:justify-between tw:bg-[#EA4335] tw:px-4 tw:py-2 tw:cursor-pointer" onClick={() => setNoticeOpen(prev => !prev)}>
                        <span className="tw:font-hanken-grotesk tw:!text-[14px] tw:font-[400] tw:text-white">
                            {t("bookingDetail.status.rejectedMessage.title")}
                        </span>
                        <button className="tw:text-white">
                            <span className={`tw:inline-flex tw:transition-transform tw:duration-300 tw:ease-in-out ${noticeOpen ? 'tw:rotate-0' : 'tw:rotate-180'}`}>
                                <HideShowIcon
                                    color="#FFFFFF"
                                    height={6}
                                    width={10}
                                />
                            </span>
                        </button>
                    </div>

                    {/* Collapsible body + footer */}
                    <div className={`tw:grid tw:transition-all tw:duration-300 tw:ease-in-out ${noticeOpen ? 'tw:grid-rows-[1fr]' : 'tw:grid-rows-[0fr]'}`}>
                        <div className="tw:overflow-hidden">
                            {/* Body */}
                            <div className="tw:flex tw:flex-col tw:gap-3 tw:px-4 tw:py-4">
                                <p className="tw:font-hanken-grotesk tw:text-[13px] tw:leading-[20px] tw:text-[#333333]"
                                    dangerouslySetInnerHTML={{ __html: t("bookingDetail.status.rejectedMessage.desc1") }}
                                >
                                </p>

                                <div>
                                    <p className="tw:font-hanken-grotesk tw:text-[13px] tw:font-semibold tw:text-[#333333]">{t("bookingDetail.status.rejectedMessage.reason")}</p>
                                    <p className="tw:font-hanken-grotesk tw:text-[13px] tw:text-[#333333]">{authDetailBooking?.requestChangeBooking[0]?.notes}</p>
                                </div>

                                <p className="tw:font-hanken-grotesk tw:text-[13px] tw:leading-[20px] tw:text-[#333333]"
                                    dangerouslySetInnerHTML={{ __html: t("bookingDetail.status.rejectedMessage.desc2") }}
                                >
                                </p>
                                <p className="tw:font-hanken-grotesk tw:text-[13px] tw:leading-[20px] tw:text-[#333333]"
                                    dangerouslySetInnerHTML={{ __html: t("bookingDetail.status.rejectedMessage.desc3") }}
                                >
                                </p>
                                <p className="tw:font-hanken-grotesk tw:text-[13px] tw:leading-[20px] tw:text-[#333333]"
                                    dangerouslySetInnerHTML={{ __html: t("bookingDetail.status.rejectedMessage.desc4") }}
                                >
                                </p>
                            </div>

                            {/* Footer */}
                            <div className="tw:flex tw:items-center tw:justify-center tw:border-t tw:!border-[#EA4335] tw:py-3">
                                <button
                                    className="tw:flex tw:items-center tw:gap-1.5 tw:font-hanken-grotesk tw:text-[13px] tw:!font-[400] tw:!text-[#EA4335] tw:underline"
                                    onClick={() => setDiscardModalOpen(true)}
                                >
                                    <CloseCircleIcon color="#EA4335" size={14} />
                                    {t("bookingDetail.status.rejectedMessage.discard")}
                                </button>
                            </div>
                        </div>
                    </div>

                    <DiscardNoticeModal
                        show={discardModalOpen}
                        onClose={() => setDiscardModalOpen(false)}
                        setNoticeShow={setNoticeShow}
                        bookingId={authDetailBooking?._id}
                        onDiscardSuccess={onDiscardSuccess}
                    />
                </div>
            )}
        </div>
    );
};

export default BookingStatus;